import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Alert } from '@/interfaces/alert';
import userServices from '@/api/userServices';

interface FilterParams {
  page: number | undefined;
  size: number | undefined;
}

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface AlertState {
  alerts: { [key: string]: Alert[] };
  params: FilterParams;
  metaData: { [key: string]: MetaData };
  currFilters: string;
  loading: boolean;
  error: string;
}

const initialState: AlertState = {
  alerts: {},
  params: {
    page: undefined,
    size: undefined,
  },
  currFilters: '',
  metaData: {},
  loading: false,
  error: '',
};

export const fetchAlerts = createAsyncThunk('alerts/fetch', async ({ size, page }: { size: number; page: number }, thunkAPI) => {
  try {
    const result = await userServices.fetchAlerts({ size, page: page - 1 });
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    refresh: state => {
      state.alerts = {};
    },
    handleParamsChange: (state, action: PayloadAction<{ size: number; page: number }>) => {
      const checkbookFilters = JSON.stringify(action.payload);
      state.currFilters = checkbookFilters;
      state.params.page = action.payload.page;
      state.params.size = action.payload.size;
    },
    updateAlertInfo: (state, action: PayloadAction<string>) => {
      const alertId = action.payload;
      const alertIndex = state.alerts[state.currFilters].findIndex(alert => alert.id === alertId);
      state.alerts[state.currFilters][alertIndex].status = 'PROCESSED';
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchAlerts.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchAlerts.fulfilled, (state, action) => {
      const { payload: data } = action;

      state.alerts[state.currFilters] = data.data;
      state.metaData[state.currFilters] = { totalPages: data.totalPages, totalElements: data.totalElements };
      state.loading = false;
      state.loading = false;
    });

    builder.addCase(fetchAlerts.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const { refresh, handleParamsChange, updateAlertInfo } = alertsSlice.actions;

export const selectAlerts = (state: RootState) => state.alerts;

export default alertsSlice.reducer;
